import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveTeamLogoRequest,
  getMyTeamBranding,
  listTeamLogoRequests,
  rejectTeamLogoRequest,
  submitMyTeamLogo,
} from '../api/portal';
import { ApiError } from '../api/client';
import { useT } from '../lib/i18n';
import { TeamCrest } from './TeamCrest';

const MAX_BYTES = 2 * 1024 * 1024;
const HEX = /^#[0-9a-f]{6}$/i;

function Swatch({ color }: { color?: string }) {
  if (!color || !HEX.test(color)) return null;
  return <span className="inline-block h-3 w-3 rounded-sm align-middle" style={{ background: color }} />;
}

/** Club login: submit a crest + colors for league approval. */
export function ClubBrandingCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const branding = useQuery({ queryKey: ['portal-team-branding'], queryFn: getMyTeamBranding });
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = useMutation({
    mutationFn: () => submitMyTeamLogo(file!, { primary, secondary }),
    onSuccess: () => {
      setMsg({ ok: true, text: t('brand.sent') });
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['portal-team-branding'] });
    },
    onError: (e) => setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) }),
  });

  const pick = (f: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!f) return;
    if (f.size > MAX_BYTES) { setMsg({ ok: false, text: t('brand.tooBig') }); return; }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setMsg(null);
  };

  const b = branding.data;
  const colorsValid = [primary, secondary].every((v) => v === '' || HEX.test(v));

  return (
    <div className="card mt-4 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('brand.title')}</div>
      <p className="mt-1 text-[12px] text-slate-500">{t('brand.sub')}</p>
      <div className="mt-3 flex flex-wrap items-start gap-6">
        <div className="text-center">
          <div className="mb-1 text-[11px] text-slate-500">{t('brand.current')}</div>
          {b?.logo_url
            ? <TeamCrest logoUrl={b.logo_url} size={56} />
            : <div className="text-xs text-slate-500">{t('brand.none')}</div>}
          <div className="mt-1 flex justify-center gap-1">
            <Swatch color={b?.colors?.primary} />
            <Swatch color={b?.colors?.secondary} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => pick(e.target.files?.[0])}
          />
          <div className="flex items-center gap-3">
            <button type="button" className="btn-secondary text-xs" onClick={() => input.current?.click()}>
              {t('brand.pick')}
            </button>
            {preview && <img src={preview} alt="" className="h-12 w-12 rounded bg-slate-900 object-contain p-1" />}
          </div>
          <div className="flex flex-wrap gap-3">
            {([['primary', primary, setPrimary], ['secondary', secondary, setSecondary]] as const).map(([k, v, set]) => (
              <label key={k} className="text-[11px] text-slate-400">
                {t(`brand.${k}`)}
                <span className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={HEX.test(v) ? v : '#000000'}
                    onChange={(e) => set(e.target.value)}
                    className="h-7 w-9 cursor-pointer rounded border border-slate-700 bg-transparent"
                  />
                  <input
                    value={v}
                    onChange={(e) => set(e.target.value.trim())}
                    placeholder="#c8102e"
                    className="input w-24 font-mono text-xs"
                  />
                </span>
              </label>
            ))}
          </div>
          <div>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={!file || !colorsValid || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? t('brand.sending') : t('brand.submit')}
            </button>
          </div>
        </div>

        {b?.pending && (
          <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-center">
            <div className="mb-1 text-[11px] text-amber-300">{t('brand.pending')}</div>
            <img src={b.pending.url} alt="" className="mx-auto h-14 w-14 object-contain" />
            <div className="mt-1 flex justify-center gap-1">
              <Swatch color={b.pending.colors?.primary} />
              <Swatch color={b.pending.colors?.secondary} />
            </div>
          </div>
        )}
      </div>
      {msg && <div className={`mt-2 text-sm ${msg.ok ? 'text-emerald-300' : 'text-rose-300'}`}>{msg.text}</div>}
    </div>
  );
}

/** League login: approve or reject crests submitted by its clubs. Renders nothing when empty. */
export function TeamLogoRequestsCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const reqs = useQuery({ queryKey: ['portal-team-logo-requests'], queryFn: listTeamLogoRequests });
  const [err, setErr] = useState<string | null>(null);
  const done = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ['portal-team-logo-requests'] });
    qc.invalidateQueries({ queryKey: ['portal-club-teams'] });
  };
  const onErr = (e: unknown) => setErr(e instanceof ApiError ? e.message : String(e));
  const approve = useMutation({ mutationFn: approveTeamLogoRequest, onSuccess: done, onError: onErr });
  const reject = useMutation({ mutationFn: rejectTeamLogoRequest, onSuccess: done, onError: onErr });

  const rows = reqs.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="card mb-4 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('brand.requests')}</div>
      <p className="mt-1 text-[12px] text-slate-500">{t('brand.requestsSub')}</p>
      <div className="mt-3 divide-y divide-slate-800">
        {rows.map((r) => (
          <div key={r.team_id} className="flex flex-wrap items-center gap-4 py-2">
            <TeamCrest logoUrl={r.logo_url} size={32} />
            <span className="text-slate-500">→</span>
            <img src={r.pending.url} alt="" className="h-10 w-10 rounded bg-slate-900 object-contain p-0.5" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="text-white">{r.team_name}</div>
              <div className="text-xs text-slate-500">
                {r.pending.club_name} · {new Date(r.pending.submitted_at).toLocaleString('sr-RS')}{' '}
                <Swatch color={r.pending.colors?.primary} /> <Swatch color={r.pending.colors?.secondary} />
              </div>
            </div>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={approve.isPending}
              onClick={() => approve.mutate(r.team_id)}
            >
              {t('brand.approve')}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={reject.isPending}
              onClick={() => reject.mutate(r.team_id)}
            >
              {t('brand.reject')}
            </button>
          </div>
        ))}
      </div>
      {err && <div className="mt-2 text-sm text-rose-300">{err}</div>}
    </div>
  );
}
